import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { sendEmailRentCinema } from '@/app/[locale]/(default-layout)/(forms)/_components/send-email';

import Button from '@/components/button/Button';
import FormCheckbox from '@/components/FormCheckbox';

import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';
import { IconSuccessful16 } from '@cineamo/ui/src/icons/ic-successful';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { track } from '@/utils/server-side-tracking';

import FormInput from './FormInput';
import FormTextarea from './FormTextarea';
import TurnstileWidget from './TurnstileWidget';

export type RentACinemaFormData = {
    preferredDate: string;
    timeFrom: string;
    timeTo: string;
    eventType: string;
    guestCount: number;
    specialRequests: string;
    firstName: string;
    lastName: string;
    company: string;
    email: string;
    phone: string;
    additionalServices: {
        foyer: boolean;
        catering: boolean;
        technical: boolean;
    };
};

export default function RentACinemaForm() {
    const t = useTranslations();
    const domainConfiguration = useDomainConfiguration();
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const [turnstileKey, setTurnstileKey] = useState(0);

    const { register, handleSubmit, watch, setValue, reset } = useForm<RentACinemaFormData>({
        defaultValues: {
            preferredDate: '',
            timeFrom: '',
            timeTo: '',
            eventType: '',
            guestCount: undefined,
            specialRequests: '',
            firstName: '',
            lastName: '',
            company: '',
            email: '',
            phone: '',
            additionalServices: {
                foyer: false,
                catering: false,
                technical: false
            }
        }
    });

    const additionalServices = watch('additionalServices');

    const onSubmit = async (data: RentACinemaFormData) => {
        setIsProcessing(true);

        try {
            const success = await sendEmailRentCinema(data, turnstileToken ?? undefined);

            if (success) {
                setIsSuccess(true);
                reset();
                showToast({
                    toastType: toast.success,
                    messageIcon: <IconSuccessful16 />,
                    messageTitle: t('rent-a-cinema.success.title'),
                    messageBody: t('rent-a-cinema.success.message'),
                    backgroundColor: domainConfiguration.style.primaryColor,
                    textColor: domainConfiguration.style.onPrimaryColor
                });
            } else {
                showToast({
                    toastType: toast.error,
                    messageTitle: t('rent-a-cinema.error.title'),
                    messageBody: t('rent-a-cinema.error.message')
                });
            }

            track('Rent Cinema Form Submitted', {
                cinema: domainConfiguration.cinema?.name,
                domain: domainConfiguration.domain,
                cinemaId: domainConfiguration.cinema?.id,
                data: JSON.stringify(data, null, 2),
                success: success
            });
        } catch {
            showToast({
                toastType: toast.error,
                messageTitle: t('rent-a-cinema.error.title'),
                messageBody: t('rent-a-cinema.error.network-message')
            });
        } finally {
            setIsProcessing(false);
            setTurnstileToken(null);
            setTurnstileKey((key) => key + 1);
        }
    };

    const handleAdditionalServiceChange = (
        service: keyof RentACinemaFormData['additionalServices'],
        checked: boolean
    ) => {
        setValue(`additionalServices.${service}`, checked);
    };

    if (isSuccess) {
        return (
            <div className="backdrop-blur-sm rounded-12 p-12 shadow-lg text-center bg-[var(--surface-color)]">
                <IconSuccessful16 className="size-[48px] mx-auto mb-4 text-[var(--primary-color)]" />
                <h3 className="text-headline-24 mb-2 text-[var(--on-background-color)]">
                    {t('rent-a-cinema.success.title')}
                </h3>
                <p className="text-body-16 text-[rgba(var(--on-background-color-rgb),0.8)]">
                    {t('rent-a-cinema.success.message')}
                </p>
            </div>
        );
    }

    return (
        <div className="backdrop-blur-sm rounded-12 p-12 shadow-lg bg-[var(--surface-color)]">
            <form
                className="space-y-24"
                onSubmit={handleSubmit(onSubmit)}>
                {/* Date and Time Selection */}
                <div className="grid md:grid-cols-3 gap-6">
                    <FormInput
                        name="preferredDate"
                        label={t('rent-a-cinema.form.preferred-date')}
                        type="date"
                        register={register}
                    />
                    <FormInput
                        name="timeFrom"
                        label={t('rent-a-cinema.form.time-from')}
                        type="time"
                        register={register}
                    />
                    <FormInput
                        name="timeTo"
                        label={t('rent-a-cinema.form.time-to')}
                        type="time"
                        register={register}
                    />
                </div>

                {/* Event Type */}
                <FormInput
                    name="eventType"
                    label={t('rent-a-cinema.form.event-type')}
                    type="text"
                    register={register}
                    placeholder={t('rent-a-cinema.form.event-type-placeholder')}
                />

                {/* Guest Count */}
                <FormInput
                    name="guestCount"
                    label={t('rent-a-cinema.form.guest-count')}
                    type="number"
                    inputMode="numeric"
                    min={1}
                    register={register}
                    registerOptions={{ valueAsNumber: true }}
                    placeholder={t('rent-a-cinema.form.guest-count-placeholder')}
                />

                {/* Additional Services */}
                <div className="space-y-3">
                    <label className="block text-body-14 font-semibold text-[var(--on-surface-color)]">
                        {t('rent-a-cinema.form.additional-services')}
                    </label>
                    <div className="space-y-3">
                        <FormCheckbox
                            name="foyer-usage"
                            label={t('rent-a-cinema.form.foyer-usage')}
                            checked={additionalServices.foyer}
                            onChange={(checked) => handleAdditionalServiceChange('foyer', checked)}
                        />
                        <FormCheckbox
                            name="catering-service"
                            label={t('rent-a-cinema.form.catering-service')}
                            checked={additionalServices.catering}
                            onChange={(checked) => handleAdditionalServiceChange('catering', checked)}
                        />
                        <FormCheckbox
                            name="technical-support"
                            label={t('rent-a-cinema.form.technical-support')}
                            checked={additionalServices.technical}
                            onChange={(checked) => handleAdditionalServiceChange('technical', checked)}
                        />
                    </div>
                </div>

                {/* Special Requests */}
                <FormTextarea
                    name="specialRequests"
                    label={t('rent-a-cinema.form.special-requests')}
                    register={register}
                    placeholder={t('rent-a-cinema.form.special-requests-placeholder')}
                />

                {/* Separator */}
                <div className="w-full h-1 bg-[rgba(var(--on-surface-color-rgb),0.3)]" />

                {/* Contact Information */}
                <div>
                    <h3 className="text-headline-20 font-semibold mb-6 text-[var(--on-surface-color)]">
                        {t('rent-a-cinema.form.contact-info')}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-8">
                        <FormInput
                            name="firstName"
                            label={t('rent-a-cinema.form.first-name')}
                            type="text"
                            register={register}
                            registerOptions={{ required: true }}
                            required
                        />
                        <FormInput
                            name="lastName"
                            label={t('rent-a-cinema.form.last-name')}
                            type="text"
                            register={register}
                            registerOptions={{ required: true }}
                            required
                        />
                        <FormInput
                            name="company"
                            label={t('rent-a-cinema.form.company')}
                            type="text"
                            register={register}
                        />
                        <FormInput
                            name="email"
                            label={t('rent-a-cinema.form.email')}
                            type="email"
                            register={register}
                            registerOptions={{ required: true }}
                            required
                        />
                        <FormInput
                            name="phone"
                            label={t('rent-a-cinema.form.phone')}
                            type="tel"
                            register={register}
                        />
                    </div>
                </div>

                {/* Separator */}
                <div className="w-full h-1 bg-[rgba(var(--on-surface-color-rgb),0.3)]" />

                {/* Required Fields Note */}
                <div>
                    <p className="text-body-12 text-center text-[var(--on-surface-color)]">
                        {t('rent-a-cinema.form.required-fields')}
                    </p>
                </div>

                {/* Submit Button */}
                <div className="pt-8">
                    <TurnstileWidget
                        key={turnstileKey}
                        onVerify={setTurnstileToken}
                    />
                    <Button
                        type="submit"
                        disabled={isProcessing}
                        className="w-full py-4 text-body-16 font-semibold transition-all duration-200 rounded-8"
                        css={{
                            backgroundColor: domainConfiguration.style.primaryColor,
                            color: domainConfiguration.style.onPrimaryColor,
                            opacity: isProcessing ? 0.6 : 1
                        }}
                        leadingIcon={isProcessing ? <IconLoading16 className="animate-spin" /> : undefined}>
                        {isProcessing ? t('rent-a-cinema.form.submitting') : t('rent-a-cinema.form.submit')}
                    </Button>
                </div>
            </form>
        </div>
    );
}
